// src/components/SezionePunti.jsx
import React, { useMemo, useState } from "react";

const SezionePunti = ({
  prodotti = [],
  prodottoAcquisto,
  quantitaAcquisto,
  righeAcquisto = [],
  scaricaPunti,
  motivoScarico,
  migrazioneQty075,
  migrazioneQty1,
  migrazioneInCorso,
  messaggio,
  setProdottoAcquisto,
  setQuantitaAcquisto,
  aggiungiRigaAcquisto,
  rimuoviRigaAcquisto,
  setScaricaPunti,
  setMotivoScarico,
  setMigrazioneQty075,
  setMigrazioneQty1,
  inviaMigrazioneTessera,
  inviaConsumo,
  inviaPagamentoWallet,
  inviaScaricoPunti
}) => {
  const [filtroCategoria, setFiltroCategoria] = useState("TUTTI");

  const categorieDisponibili = useMemo(() => {
    return [...new Set(
      prodotti
        .map((p) => String(p.category || p.categoria || p.product_category || "").trim())
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, "it"));
  }, [prodotti]);

  const prodottiFiltrati = useMemo(() => {
    const norm = (v) => String(v || "").trim().toUpperCase();

    return prodotti.filter((p) => {
      const name = norm(p.name);
      const category = norm(p.category || p.categoria || p.product_category);
      const unitType = norm(p.unit_type);
      const litri = Number(p.litri_per_unita || 0);

      if (filtroCategoria === "TUTTI") return true;
      if (filtroCategoria === "SFUSO_075") {
        return category === "SFUSO DA 0,75" ||
          (unitType === "LITRI" && litri > 0 && litri <= 0.8);
      }
      if (filtroCategoria === "SFUSO_1L") {
        return category === "SFUSO DA LITRO" &&
          (litri === 0 || (litri >= 0.9 && litri <= 1.1));
      }
      if (filtroCategoria === "DAME_PET") {
        return name.includes("DAMA") || name.includes("PET") ||
          (category === "SFUSO DA LITRO" && litri >= 2);
      }
      if (filtroCategoria === "BAG_BOX") {
        return category === "BAG IN BOX" ||
          name.includes("BAG IN BOX") || name.includes("BAG-IN-BOX");
      }
      if (filtroCategoria.startsWith("CAT:")) {
        return category === norm(filtroCategoria.slice(4));
      }

      return true;
    });
  }, [prodotti, filtroCategoria]);

  const selected = useMemo(
    () => prodotti.find((p) => String(p.id) === String(prodottoAcquisto)),
    [prodotti, prodottoAcquisto]
  );

  const qty = Number(quantitaAcquisto || 0);
  const litriPerUnita = Number(selected?.litri_per_unita || 0);
  const prezzo = Number(selected?.prezzo || 0);
  const puntiStimati = selected?.unit_type === "litri" ? qty * litriPerUnita : 0;
  const totaleStimato = qty * prezzo;

  const totaleCarrello = righeAcquisto.reduce(
    (sum, r) => sum + (Number(r.prezzo || 0) * Number(r.quantity || 0)),
    0
  );

  const puntiCarrello = righeAcquisto.reduce(
    (sum, r) => sum + (r.unit_type === "litri" ? Number(r.litri_per_unita || 0) * Number(r.quantity || 0) : 0),
    0
  );

  const puntiMigrazione =
    (Number(migrazioneQty075 || 0) * 0.75) +
    Number(migrazioneQty1 || 0);

  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body">
        <h4 className="mb-1">Registra acquisto cliente</h4>
        <div className="text-muted small mb-3">
          Aggiungi uno o più prodotti dal listino clienti, poi registra l'acquisto completo. I punti vengono caricati solo sui prodotti in litri.
        </div>

        <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label">Filtro rapido</label>
              <select
                className="form-select"
                value={filtroCategoria}
                onChange={(e) => {
                  setFiltroCategoria(e.target.value);
                  setProdottoAcquisto("");
                }}
              >
                <option value="TUTTI">Tutti i prodotti</option>
                <option value="SFUSO_075">Sfuso 0,75 L</option>
                <option value="SFUSO_1L">Sfuso 1 L</option>
                <option value="DAME_PET">Dame / PET</option>
                <option value="BAG_BOX">Bag in box</option>
                {categorieDisponibili.map((categoria) => (
                  <option key={categoria} value={`CAT:${categoria}`}>
                    {categoria}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-5">
              <label className="form-label">Prodotto / vino</label>
              <select
                className="form-select"
                value={prodottoAcquisto}
                onChange={(e) => setProdottoAcquisto(e.target.value)}
              >
                <option value="">Seleziona dal listino clienti...</option>
                {prodottiFiltrati.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — €{Number(p.prezzo || 0).toFixed(2)}
                    {p.unit_type === "litri" ? ` — ${Number(p.litri_per_unita || 0).toFixed(3)}L cad.` : ""}
                  </option>
                ))}
              </select>
            </div>

          <div className="col-12 col-md-2">
            <label className="form-label">Quantità</label>
            <input
              type="number"
              min="0"
              step="1"
              placeholder="Es. 10"
              className="form-control"
              value={quantitaAcquisto}
              onChange={(e) => setQuantitaAcquisto(e.target.value)}
            />
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label">Totale €</label>
            <div className="form-control bg-light fw-bold">{totaleStimato.toFixed(2)}</div>
          </div>

          <div className="col-6 col-md-2">
            <label className="form-label">Punti</label>
            <div className="form-control bg-light fw-bold">{puntiStimati.toFixed(2)}</div>
          </div>

          <div className="col-12 col-md-3">
            <button className="btn btn-outline-primary w-100" onClick={aggiungiRigaAcquisto}>
              + Aggiungi prodotto
            </button>
          </div>
        </div>

        {righeAcquisto.length > 0 && (
          <div className="mt-4">
            <h5>Prodotti da registrare</h5>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Prodotto</th>
                    <th className="text-end">Q.tà</th>
                    <th className="text-end">Totale €</th>
                    <th className="text-end">Punti</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {righeAcquisto.map((r, idx) => {
                    const rowTotal = Number(r.prezzo || 0) * Number(r.quantity || 0);
                    const rowPoints = r.unit_type === "litri"
                      ? Number(r.litri_per_unita || 0) * Number(r.quantity || 0)
                      : 0;
                    return (
                      <tr key={`${r.product_id}-${idx}`}>
                        <td>{r.product_name}</td>
                        <td className="text-end">{Number(r.quantity || 0).toFixed(2)}</td>
                        <td className="text-end">€{rowTotal.toFixed(2)}</td>
                        <td className="text-end">{rowPoints.toFixed(2)}</td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-danger" onClick={() => rimuoviRigaAcquisto(idx)}>
                            Elimina
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th>Totale</th>
                    <th></th>
                    <th className="text-end">€{totaleCarrello.toFixed(2)}</th>
                    <th className="text-end">{puntiCarrello.toFixed(2)}</th>
                    <th></th>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-primary" onClick={inviaConsumo}>
                Pagamento in contanti / carta presso punto vendita
              </button>
              <button className="btn btn-success" onClick={inviaPagamentoWallet}>
                💰 Paga con Credito App / Wallet
              </button>
            </div>
          </div>
        )}

        <hr className="my-4" />

        <div className="border rounded-4 p-3 mb-4 bg-light">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
            <div>
              <h5 className="mb-1">Migrazione tessera precedente</h5>
              <div className="text-muted small">
                Trasferisci nel nuovo sistema i punti presenti sulla vecchia tessera del cliente.
                Questa operazione non registra una nuova vendita o un nuovo consumo.
              </div>
            </div>
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label">Quantit? da 0,75 L</label>
              <input
                type="number"
                min="0"
                step="1"
                className="form-control"
                placeholder="Es. 8"
                value={migrazioneQty075}
                onChange={(e) => setMigrazioneQty075(e.target.value)}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">Quantit? da 1 L</label>
              <input
                type="number"
                min="0"
                step="1"
                className="form-control"
                placeholder="Es. 3"
                value={migrazioneQty1}
                onChange={(e) => setMigrazioneQty1(e.target.value)}
              />
            </div>

            <div className="col-12 col-md-3">
              <label className="form-label">Punti da migrare</label>
              <div className="form-control bg-white fw-bold">
                {puntiMigrazione.toFixed(2)}
              </div>
            </div>

            <div className="col-12 col-md-3">
              <button
                type="button"
                className="btn btn-warning w-100"
                onClick={inviaMigrazioneTessera}
                disabled={migrazioneInCorso || puntiMigrazione <= 0}
              >
                {migrazioneInCorso ? "Migrazione in corso..." : "Conferma migrazione"}
              </button>
            </div>
          </div>

          <div className="small text-muted mt-3">
            Calcolo: quantit? 0,75 L ? 0,75 punti + quantit? 1 L ? 1 punto.
            La migrazione pu? essere effettuata una sola volta per cliente.
          </div>
        </div>

        <h5 className="mb-3">Scarica punti manualmente</h5>
        <div className="row g-2 align-items-end">
          <div className="col-12 col-md-3">
            <label className="form-label">Punti da scaricare</label>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder="Es. 50"
              className="form-control"
              value={scaricaPunti}
              onChange={(e) => setScaricaPunti(e.target.value)}
            />
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label">Motivo</label>
            <input
              type="text"
              placeholder="Es. Premio 1 litro omaggio"
              className="form-control"
              value={motivoScarico}
              onChange={(e) => setMotivoScarico(e.target.value)}
            />
          </div>

          <div className="col-12 col-md-3">
            <button className="btn btn-danger w-100" onClick={inviaScaricoPunti}>
              Scarica punti
            </button>
          </div>
        </div>

        {messaggio && (
          <div className="alert alert-info mt-3 mb-0">
            {messaggio}
          </div>
        )}
      </div>
    </div>
  );
};

export default SezionePunti;
